import EntityService, { WithSubcollection} from '@/services/EntityService'
import { Project, projectConverter } from '@/models/project'
import { Message, messageConverter } from '@/models/message'
import { useAuth } from '@/composables/useAuth'
import { query, or, where, getDocs, limit, orderBy, doc, setDoc, collection, deleteDoc } from 'firebase/firestore'
import FirebaseService from './FirebaseService'
import BudgetService from './BudgetService'
import MessageService from './MessageService'

@WithSubcollection('messages', messageConverter)
export default class ProjectService extends EntityService<Project> {
  constructor() {
    super({ collectionName: 'projects', converter: projectConverter })
  }

  async addMessageToProject(projectId: string, customerId: string, message: Message) {
    const db = await FirebaseService.firestore()
    const record = doc(collection(db,  `customers/${customerId}/${this.collectionName}/${projectId}/messages`)).withConverter(messageConverter)
    const newMessage = {
      ...message,
      id: record.id,
      deleted: false
    }

    await setDoc(record, newMessage, { merge: true })

    return Object.assign(message, { id: record.id }) as Message
  }

  async getProjectMessages(projectId: string, customerId: string) {
    const db = await FirebaseService.firestore()
    const collectionRef = collection(db, `customers/${customerId}/${this.collectionName}/${projectId}/messages`).withConverter(messageConverter)
    const records = (await getDocs(collectionRef)).docs

    return records.map(doc => doc.data())
  }

  async getUserProjects() {
    const { currentUser } = useAuth()
    const collectionRef = await this.getCollectionGroup()
    const queryRef = query(
      collectionRef,
      or(
        where('customerId', '==', currentUser.value.id),
        where('salesPersonId', '==', currentUser.value.id),
        where('projectManagerId', '==', currentUser.value.id),
        where('estimatorId', '==', currentUser.value.id)
      ),
      orderBy('createdAt', 'desc'),
      limit(100)
    )

    const querySnapshot = await getDocs(queryRef)

    return querySnapshot.docs.map(doc => doc.data() as Project)
  }

  async updateProjectStatus(id: string, customerId: string, status: string) {
    const db = await FirebaseService.firestore()
    const record = doc(db, `customers/${customerId}/${this.collectionName}/${id}`).withConverter(this.converter)
    await setDoc(record, {
      status,
      updatedAt: new Date()
    }, { merge: true })

    return true
  }

  async moveMessagesToNewCustomer(projectId: string, oldCustomerId: string, newCustomerId: string) {
    const db = await FirebaseService.firestore()
    const collectionRef = collection(db, `customers/${oldCustomerId}/${this.collectionName}/${projectId}/messages`).withConverter(messageConverter)
    const records = (await getDocs(collectionRef)).docs

    records.forEach(async (message) => {
      const record = doc(db, `customers/${newCustomerId}/${this.collectionName}/${projectId}/messages/${message.id}`).withConverter(messageConverter)
      await setDoc(record, { ...message.data(), isEmailSent: true}, { merge: true })
      await deleteDoc(message.ref)
    })

    return true
  }


  async deleteProject(projectId: string, customerId: string): Promise<boolean> {
    const budgetService = new BudgetService()
    const budget = await budgetService.find(projectId)
    let isBudgetDeleted = true

    if (budget) {
      isBudgetDeleted = await budgetService.deleteBudget(budget, customerId, true)
    }

    if (isBudgetDeleted) {
      const messageService = new MessageService(`customers/${customerId}/projects/${projectId}`)
      await messageService.deleteAll()

      const db = await FirebaseService.firestore()
      const record = doc(db, `customers/${customerId}/${this.collectionName}/${projectId}`).withConverter(this.converter)
      await deleteDoc(record)
    }

    return true
  }
}
